import type { FC } from 'react';
import { Card, Flex, Typography } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
import type { RepairHoursViewModel } from '../../OrganizationManagerDashboardPage.types';
import { barHeightPercent, targetLinePercent } from '../../utils';

const { Text, Title } = Typography;

export interface QuarterlyRepairHoursCardProps {
  repair: RepairHoursViewModel;
}

export const QuarterlyRepairHoursCard: FC<QuarterlyRepairHoursCardProps> = ({
  repair,
}) => {
  const targetPct =
    repair.targetHours == null
      ? null
      : targetLinePercent(repair.targetHours, repair.maxHours);

  return (
    <Card className="om-section-card" styles={{ body: { padding: 24 } }}>
      <Flex vertical gap={20} style={{ height: '100%' }}>
        <div>
          <Title level={5} className="ds-section-header" style={{ margin: 0 }}>
            {repair.title}
          </Title>
          <Text type="secondary" className="om-section-subtitle">
            {repair.subtitle}
          </Text>
        </div>

        <div className="om-repair-chart">
          <div className="om-repair-chart__plot">
            {targetPct != null && repair.targetLineLabel ? (
              <div
                className="om-repair-chart__target"
                style={{ bottom: `${targetPct}%` }}
              >
                <span className="om-repair-chart__target-line" />
                <Text className="om-repair-chart__target-label">
                  {repair.targetLineLabel}
                </Text>
              </div>
            ) : null}

            <Flex
              justify="space-around"
              align="flex-end"
              className="om-repair-chart__bars"
            >
              {repair.bars.map((bar) => {
                const height =
                  bar.hours == null
                    ? null
                    : barHeightPercent(bar.hours, repair.maxHours);
                return (
                  <Flex
                    key={bar.id}
                    vertical
                    align="center"
                    justify="flex-end"
                    gap={6}
                    className="om-repair-chart__bar-col"
                  >
                    <Text className="om-repair-chart__value">
                      {bar.hoursLabel}
                    </Text>
                    <div
                      className={
                        bar.highlighted
                          ? 'om-repair-chart__bar om-repair-chart__bar--active'
                          : 'om-repair-chart__bar'
                      }
                      style={{
                        height:
                          height == null ? '0%' : `${Math.max(height, 6)}%`,
                      }}
                    />
                  </Flex>
                );
              })}
            </Flex>
          </div>

          <Flex justify="space-around" className="om-repair-chart__labels">
            {repair.bars.map((bar) => (
              <Text
                key={bar.id}
                type="secondary"
                className="ds-small-label om-repair-chart__label"
              >
                {bar.label}
              </Text>
            ))}
          </Flex>
        </div>

        {repair.insight ? (
          <div className="om-repair-insight">
            <InfoCircleOutlined
              className="om-repair-insight__icon"
              aria-hidden
            />
            <Text className="om-repair-insight__text">{repair.insight}</Text>
          </div>
        ) : null}
      </Flex>
    </Card>
  );
};
